import { useEffect, useState } from 'react';
import { useContractsQuery } from './contracts.generated';
import { CombinedError } from '@web/api/src';
import { filterNulls } from '@web/utils/src';
import { Contract, ContractLineItem, ContractStatus, Retailer } from '@/types';

type PContractLineItem = Pick<
  ContractLineItem,
  'deliveredAmount' | 'amount' | 'erpAmountUnit'
>;

export type PContract = Pick<
  Contract,
  'createdAt' | 'id' | 'erpID' | 'status' | 'type' | 'expectedDeliveryAt'
> & {
  retailer: Pick<Retailer, 'name' | 'id'>;
} & {
  lineItems: Array<PContractLineItem>;
};

interface ContractsPaginationReturn {
  result: PContract[];
  fetching: boolean;
  error: CombinedError | undefined;
  loadMore: () => void;
  loadPrevious: () => void;
  isReachingEnd: boolean;
  hasPreviousPage: boolean;
  cursors: (string | undefined)[];
  total: number;
  refetch: () => void;
}

const useUserContractsPagination = (
  retailerId: string | null,
  perPage: number,
  statusFilter: ContractStatus[] | null,
): ContractsPaginationReturn => {
  const [after, setAfter] = useState('0');
  const [cursors, setCursors] = useState<(string | undefined)[]>([]);
  const [result, setResult] = useState<Array<PContract>>([]);

  const refetch = () => {
    setResult([]);
    setCursors([]);
    setAfter('0');
  };

  useEffect(() => {
    refetch();
  }, [retailerId, statusFilter, statusFilter?.length]);

  const effectiveStatusFilter =
    statusFilter && statusFilter.length > 0
      ? statusFilter
      : Object.values(ContractStatus).filter((s) => s !== ContractStatus.Draft);

  const [{ data, error, fetching }] = useContractsQuery({
    variables: {
      filter: {
        retailer: retailerId,
        contractStatus: effectiveStatusFilter,
      },
      after,
      first: perPage,
    },
    pause: after === '0' && result.length > 0,
  });

  const edges = data?.currentUser?.contractsConnection?.edges || [];
  const isReachingEnd = edges.length !== perPage || fetching;
  const hasPreviousPage = cursors.length > 0;

  const endCursor = data?.currentUser?.contractsConnection?.pageInfo.endCursor;

  const loadMore = () => {
    if (isReachingEnd) return;
    const newCursors = [
      ...cursors,
      data?.currentUser?.contractsConnection.pageInfo.endCursor,
    ];
    setCursors(newCursors || []);
    setAfter(endCursor ?? '0');
  };

  const loadPrevious = () => {
    if (!hasPreviousPage) return;
    const newCursors = cursors.slice(0, -1);
    setCursors(newCursors);
    const prevCursor = newCursors[newCursors.length - 1] || '0';
    setAfter(prevCursor);
  };

  const total = data?.currentUser?.contractsConnection?.pageInfo?.total || 0;

  useEffect(() => {
    if (data?.currentUser?.contractsConnection?.edges) {
      const newLineItemsResult = filterNulls(
        data.currentUser.contractsConnection.edges.map(
          (edge) => edge.node || null,
        ),
      );

      setResult((prevState) => {
        // Create a map to prevent duplicates based on `id`
        const existingIds = new Set(prevState.map((item) => item.id));
        const uniqueItems = newLineItemsResult.filter(
          (item) => !existingIds.has(item.id),
        );

        // Append only new unique items to the state
        return [...prevState, ...uniqueItems];
      });
    }
  }, [data]);

  return {
    result,
    fetching,
    error,
    loadMore,
    loadPrevious,
    isReachingEnd,
    hasPreviousPage,
    cursors,
    total,
    refetch,
  };
};

export default useUserContractsPagination;
